import { Navigate, Route, Routes } from 'react-router-dom';
import { App as AntdApp } from 'antd';
import { AppShell } from '@/components/AppShell';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { Callback } from '@/auth/Callback';
import { FlowsListPage } from '@/pages/FlowsListPage';
import { DesignerPage } from '@/pages/DesignerPage';
import { MigrationReviewPage } from '@/pages/MigrationReviewPage';
import { WorkbenchPage } from '@/pages/WorkbenchPage';
import { EntityDetailPage } from '@/pages/EntityDetailPage';
import { AdminPage } from '@/pages/AdminPage';
import { OversightPage } from '@/pages/OversightPage';
import { IntegrationsPage } from '@/pages/IntegrationsPage';
import { TemplatesPage } from '@/pages/TemplatesPage';
import { RunsPage } from '@/pages/RunsPage';
import { BulkPage } from '@/pages/BulkPage';
import { BulkBatchPage } from '@/pages/BulkBatchPage';
import { CaseDetailPage } from '@/pages/CaseDetailPage';
import { EntitiesPage } from '@/pages/EntitiesPage';
import { SimulationsPage } from '@/pages/SimulationsPage';
import { ScreenStudioPage } from '@/pages/ScreenStudioPage';
import { AnalyticsPage } from '@/pages/AnalyticsPage';
import { AlertsPage } from '@/pages/AlertsPage';

/**
 * Routes. The theme + AntD ConfigProvider live above this (ThemedRoot, main.tsx) so the auth gate is
 * themed too. `/flows/new` is served by the DesignerPage (flowId === 'new' scaffolds a blank flow).
 */
export default function App() {
  return (
    <AntdApp style={{ height: '100%' }}>
      <Routes>
        {/* Unauthenticated landing for the OIDC redirect. */}
        <Route path="/auth/callback" element={<Callback />} />

        {/* Everything else is behind auth (reject-closed). */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppShell />}>
            <Route index element={<Navigate to="/workbench" replace />} />
            <Route path="/workbench" element={<WorkbenchPage />} />
            <Route path="/flows" element={<FlowsListPage />} />
            <Route path="/flows/:flowId" element={<DesignerPage />} />
            <Route path="/migration" element={<MigrationReviewPage />} />
            <Route path="/runs" element={<RunsPage />} />
            <Route path="/bulk" element={<BulkPage />} />
            <Route path="/bulk/:batchId" element={<BulkBatchPage />} />
            <Route path="/cases/:runId" element={<CaseDetailPage />} />
            <Route path="/entities" element={<EntitiesPage />} />
            <Route path="/entities/:entityId" element={<EntityDetailPage />} />
            <Route path="/simulations" element={<SimulationsPage />} />
            {/* P3 — the screen studio (vendor implementation team in v1; screens.read gates). */}
            <Route path="/screens" element={<ScreenStudioPage />} />
            {/* P5 — read-only analytics (analytics.read; sources-honesty, ACL-narrowed). */}
            <Route path="/analytics" element={<AnalyticsPage />} />
            {/* P4 — the alert centre register + routing rules (alerts.read / alerts.rules.*). */}
            <Route path="/alerts" element={<AlertsPage />} />
            {/* PS-4 — tenant admin / hub oversight / integrations / templates. The API enforces the
                hub_admin/platform role; the UI gates the nav visually but never relies on it. */}
            <Route path="/templates" element={<TemplatesPage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/oversight" element={<OversightPage />} />
            <Route path="/admin" element={<AdminPage />} />
            {/* Legacy /settings placeholder now superseded by /admin. */}
            <Route path="/settings" element={<Navigate to="/admin" replace />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/workbench" replace />} />
      </Routes>
    </AntdApp>
  );
}
